<template>
  <default-layout show-leaderboard-ad>
    <div class="forum-thread" data-test-id="forum-thread">
      <div class="forum-thread__action-bar">
        <router-link
          :to="{ name: 'forums' }"
          class="forum-thread__back"
          data-test-id="forum-thread-back"
        >
          <icon-component
            name="caret"
            title="caret icon"
            class="forum-thread__back-icon icon--small"
          />
          <span class="forum-thread__back-text">
            Back to all discussions
          </span>
        </router-link>
        <div class="forum-thread__cta">
          <search-form
            class="forum-thread__search"
            placeholder="Search discussions"
            @search="handleSearch"
          />
          <button
            class="forum-thread__button button"
            @click="handleDiscussionClick"
          >
            Start a discussion
          </button>
        </div>
      </div>
      <loading-spinner v-if="loading !== 0" />
      <div v-else-if="thread" class="forum-thread__content" data-test-id="forum-thread-content">
        <div class="forum-thread__user-actions">
          <bookmark-button
            :bookmarkable="thread"
            show-text
          />
          <subscription-button
            :subscribable="thread"
          />
        </div>
        <div class="forum-thread__header">
          <h1
            id="forum-thread-heading"
            class="forum-thread__heading"
            data-test-id="forum-thread-header"
          >
            {{ thread.name | decodeHtml }}
          </h1>
          <user-byline
            :user="thread.user"
            :date="thread.insertedAt"
            :show-date="true"
            :show-preposition="false"
          />
          <lazy-hydrate never>
            <div class="forum-thread__body" data-test-id="forum-thread-body" v-html="thread.body" />
          </lazy-hydrate>
          <div class="forum-thread__header-bottom">
            <tag-list
              v-if="thread.tags.length"
              :tags="thread.tags"
            />
            <div class="forum-thread__engagement" data-test-id="forum-thread-engagement">
              <confirmation-dialog
                v-if="$store.state.currentUser.user.openConfirmationDialog"
                ref="confirmationDialog"
                :heading="confirmationDialogHeading"
                @confirm="deleteThread"
              />
              <engagement-reply-bar
                object-type="thread"
                :comment="thread"
                :the-object="thread"
                :is-parent-thread="true"
                @delete="$store.dispatch('openConfirmationDialog')"
                @openForm="openThreadForm"
              />
            </div>
          </div>
        </div>
        <div id="forum-thread-discussion" class="forum-thread__discussion">
          <thread-discussion
            ref="threadDiscussion"
            :thread="thread"
            :replies="thread.replies"
            @loaded="threadDiscussionLoaded"
          />
          <div v-if="thread.isClosed" class="forum-thread__closed" data-test-id="forum-thread-closed">
            This post has been closed to new replies.
          </div>
          <div v-if="showThreadForm">
            <thread-form
              ref="threadForm"
              :mentionable="thread"
              :parent-thread-id="thread.id"
              @submitted="handleReplySubmitted"
              @firstReply="handleFirstReply"
            />
          </div>
          <div v-else-if="!thread.isClosed">
            <div class="forum-thread__prompt">
              <button
                type="button"
                class="link"
                aria-haspopup="true"
                @click="openLoginDialog"
              > Log in
              </button>
              or
              <router-link
                :to="{ name: 'register', query: { redirect: this.$route.fullPath } }"
                rel="nofollow"
              >create an account</router-link>
              to reply.
            </div>
          </div>
        </div>
      </div>
      <loading-spinner v-if="loading !== 0" />
      <recommended-threads-section v-else-if="thread" :thread-id="thread.id" />
      <transition name="fade">
        <icon-component
          v-if="showBackToTop"
          container="button"
          name="caret"
          class="forum-thread__back-to-top"
          title="back to top"
          aria-label="back to top"
          @click="backToTop()"
        />
      </transition>
    </div>
  </default-layout>
</template>

<script>
import throttle from 'lodash/throttle';
import EngagementReplyBar from '@/components/Engagement/EngagementReplyBar.vue';
import SearchForm from '@/components/Search/SearchForm.vue';
import TagList from '@/components/Forum/TagList.vue';
import UserByline from '@/components/User/UserByline.vue';
import SubscriptionButton from '@/components/Subscription/SubscriptionButton.vue';
import BookmarkButton from '@/components/Bookmark/BookmarkButton.vue';
import ThreadDiscussion from '@/components/Thread/ThreadDiscussion.vue';
import ThreadForm from '@/components/Thread/ThreadForm.vue';
import RecommendedThreadsSection from '@/components/Forum/RecommendedThreadsSection.vue';
import ConfirmationDialog from '@/components/User/ConfirmationDialog.vue';
import { GET_SINGLE_THREAD_QUERY } from '@/graphql/queries/forum-queries';
import { DELETE_THREAD } from '@/graphql/mutations/thread-mutation';
import { threadJsonLd } from '@/utils/structured-data';
import { mapGetters } from 'vuex';
import scrollToMixin from '@/mixins/scroll-to-mixin';

export default {
  name: 'ForumThread',
  components: {
    SearchForm,
    EngagementReplyBar,
    TagList,
    UserByline,
    SubscriptionButton,
    BookmarkButton,
    ThreadDiscussion,
    ThreadForm,
    RecommendedThreadsSection,
    ConfirmationDialog,
  },
  mixins: [
    scrollToMixin,
  ],
  data() {
    return {
      loading: 0,
      thread: undefined,
      threadId: this.$route.params.id || null,
      slug: this.$route.params.slug || null,
      showBackToTop: false,
      scrollHandler: throttle(this.handleScroll, 250),
    };
  },
  computed: {
    ...mapGetters([
      'refetchForumIndex',
    ]),
    page() {
      return (this.$route.query.page && parseInt(this.$route.query.page, 10)) || 1;
    },
    showThreadForm() {
      return !this.thread.isClosed;
    },
    confirmationDialogHeading() {
      return 'Are you sure you want to delete this thread?';
    },
  },
  watch: {
    thread(to) {
      if (!to) { return; }
      this.onLoad();
    },
  },
  mounted() {
    this.setDataLayer();
    window.addEventListener('scroll', this.scrollHandler);
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.scrollHandler);
  },
  methods: {
    handleDiscussionClick() {
      this.$router.push({ name: 'createThread' });
    },
    threadDiscussionLoaded() {
      if (this.$route.query.scroll === true) {
        this.$nextTick(() => {
          this.scrollTo('#forum-thread-discussion', 'instant');
        });
      }
    },
    openThreadForm() {
      this.$refs.threadForm.$refs.mentionBox.openMentionbox();
    },
    handleReplySubmitted(replyData) {
      this.$refs.threadDiscussion.showMoreReplies();
      this.$nextTick(() => {
        this.scrollTo(`#reply-${replyData.id}`);
      });
    },
    handleSearch(term) {
      this.$router.push({ name: 'forums', query: { term } });
    },
    deleteThread() {
      this.$apollo.mutate({
        mutation: DELETE_THREAD,
        variables: {
          id: this.thread.id,
        },
      }).then(() => {
        this.$store.dispatch('setRefetchForumIndex', true);
        this.$refs.confirmationDialog.close();
        this.$store.dispatch('addToastNotification', {
          toastType: 'success',
          description: 'Your thread was deleted',
          type: 'Forum',
        });
        window.setTimeout(() => {
          this.$router.push({ name: 'forums' });
        }, 600);
      }).catch(() => {
        this.$store.dispatch('addGenericErrorNotification');
      });
    },
    openLoginDialog(event) {
      this.$store.dispatch('openLoginDialog', { isUGC: true });
      this.$snowplow.trackButtonEvent({
        data: {
          type: 'log in',
          text: event.target.textContent.trim(),
          classes: event.target.classList,
          id: event.target.id,
        },
      });
    },
    handleFirstReply() {
      this.$apollo.queries.thread.refetch();
    },
    setJsonLd() {
      this.$store.dispatch(
        'setContentJsonLd',
        threadJsonLd(
          this.thread,
          this.$route.path,
          this.$root.hostname,
          this.$site.settings.condition_name,
        ),
      );
    },
    setDataLayer() {
      const dataLayer = {
        post_categories: ['forums'],
        has_user_generated_content: true,
      };
      this.$emit('loaded', dataLayer);
    },
    onLoad() {
      this.setJsonLd();
      this.$store.dispatch('updateGlobalContext', {
        page: {
          id: `thread-${this.thread.id}`,
        },
      });
    },
    handleScroll() {
      const threshold = window.innerHeight * 3;
      this.showBackToTop = window.scrollY > threshold;
    },
    backToTop() {
      this.scrollTo('#forum-thread-heading');
    },
  },
  metaInfo() {
    let metaObject = {};
    if (this.loading || !this.thread || !this.thread.seo) { return metaObject; }
    if (!this.$store.getters.siteHasFeature('Forums')) {
      metaObject = {
        meta: [
          {
            name: 'robots',
            vmid: 'robots',
            content: 'noindex',
          },
        ],
        link: [
          { vmid: 'canonical', content: null },
        ],
      };
      return metaObject;
    }
    metaObject = {
      title: this.$root.$options.filters.decodeHtml(this.thread.seo.title || this.thread.name),
      titleTemplate: '%s',
      meta: [
        {
          vmid: 'description',
          name: 'description',
          content: this.thread.seo.metadesc,
        },
        {
          vmid: 'keywords',
          name: 'keywords',
          content: this.thread.seo.metakeywords,
        },
        {
          vmid: 'og:title',
          property: 'og:title',
          content: this.thread.seo.opengraphTitle,
        },
        {
          vmid: 'og:description',
          property: 'og:description',
          content: this.thread.seo.opengraphDescription,
        },
        { vmid: 'og:image', property: 'og:image', content: this.thread.seo.opengraphImage },
        {
          vmid: 'twitter:title',
          name: 'twitter:title',
          content: this.thread.seo.twitterTitle,
        },
        {
          vmid: 'twitter:description',
          name: 'twitter:description',
          content: this.thread.seo.twitterDescription,
        },
        {
          vmid: 'twitter:image',
          name: 'twitter:image',
          content: this.thread.seo.twitterImage,
        },
      ],
      link: [],
    };
    if (this.thread.seo.canonical) {
      metaObject.link.push({ vmid: 'canonical', rel: 'canonical', href: this.thread.seo.canonical });
    }
    if (this.thread.seo.metaRobotsNoindex) {
      metaObject.meta.push({ vmid: 'robots', name: 'robots', content: 'noindex' });
    }
    return metaObject;
  },
  apollo: {
    thread: {
      query: GET_SINGLE_THREAD_QUERY,
      variables() {
        return {
          id: this.threadId,
          slug: this.slug,
        };
      },
      result({ data }) {
        if (data) {
          if (!process.browser) {
            // make sure that we set the jsonLD
            // for the page during SSR
            this.setJsonLd();
          }
        }
      },
    },
  },
};
</script>

<style lang="scss" scoped>
  @import '@/stylesheets/components/_forum-thread';
</style>

<style lang="scss"> // no scoped - to reach HTML content
  @import '@/stylesheets/components/_uploaded-image';
  /**
   * 1. Element selectors used due to v-html content from wordpress.
   */
  .forum-thread__body p { /* [1] */
    margin-bottom: $spacing * 3;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .forum-thread__body a:focus {
    @include highlighter-focus();
  }
</style>
